// src/components/membership/MembershipCarousel.js
// =============================================================================
// MembershipCarousel (controls-only parent)
// - Renders only:
//    - MembershipPlansMatrix
// - Subscription status + features come from MembershipPlans-SubscriptionStatus
// =============================================================================

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { MembershipPlansMatrix } from "./MembershipPlans-Matrix.js";
import "./MembershipCarousel.css";
import "./MembershipPlans-Matrix.css";
import "./MembershipPlans-ComparePlans.css";

import {
    PLAN_ID,
    PLAN_RANK,
    STRIPE_PRICE_ID,
    useMembershipSubscriptionStatus,
    isBlackGoldPeerLocked,
    getOilBaronUpsellCheckoutItems,
} from "./MembershipPlans-SubscriptionStatus.js";

const TEXT = {
    subscriptionsLoadFailed: "Subscription status could not be loaded. Displaying default plan options.",
    loadingSubscriptions: "Loading subscription status…",
    btnClose: "Close",
    tabCompare: "Compare plan",
    legendYouHave: "You have this",
    legendTheyHave: "New plan features",
    matrixFeature: "Feature",
    matrixUnavailableCta: "Unavailable",
    matrixCurrentPlanCta: "Current Plan",
    matrixUpgradePrefix: "Upgrade for",
    matrixUpgradeFallback: "Upgrade",
    matrixCustomCta: "Contact us",
};

const CONFIG = {
    size: {
        stageH: { min: 320, max: 400, ratio: 0.36 },
    },
    uiScale: {
        baseStageH: 520,
        min: 0.72,
        max: 1,
    },
};

function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
}

function normalizePlanId(planId) {
    const normalized = String(planId || "").trim().toLowerCase().replace(/[_\s]+/g, "-");
    if (!normalized) return "";
    if (normalized === PLAN_ID.wildcatter) return PLAN_ID.wildcatter;
    if (normalized === PLAN_ID.blackgoldRevenue || normalized === "blackgoldrevenue") return PLAN_ID.blackgoldRevenue;
    if (normalized === PLAN_ID.blackgoldJib || normalized === "blackgoldjib") return PLAN_ID.blackgoldJib;
    if (normalized === PLAN_ID.oilBaron || normalized === "oilbaron") return PLAN_ID.oilBaron;
    if (normalized === PLAN_ID.bigRig || normalized === "bigrig") return PLAN_ID.bigRig;
    return normalized;
}

function normalizeAccountTypeKey(...values) {
    for (const value of values) {
        const raw = value?.accountType ?? value?.AccountType ?? value?.accountTypeName ?? value?.AccountTypeName ?? value?.type ?? value?.Type ?? value?.accountTypeId ?? value?.AccountTypeId ?? value;
        if (raw === null || raw === undefined) continue;

        const normalized = String(raw).trim().toLowerCase();
        if (!normalized) continue;

        if (normalized === "1" || normalized === "admin") return "admin";
        if (normalized === "2" || normalized === "sender" || normalized === "operator") return "sender";
        if (normalized === "3" || normalized === "receiver" || normalized === "owner") return "receiver";
        if (normalized === "4" || normalized === "senderreceiver" || normalized === "sender-receiver" || normalized === "sender_receiver" || normalized === "sender receiver" || normalized === "sender/receiver" || normalized === "operatorowner" || normalized === "operator-owner" || normalized === "operator_owner" || normalized === "operator owner" || normalized === "operator/owner") return "senderreceiver";
    }

    return "";
}

function isOperatorAccountTypeId(accountTypeKey) {
    return accountTypeKey === "admin" || accountTypeKey === "sender" || accountTypeKey === "senderreceiver";
}

function isOwnerAccountTypeId(accountTypeKey) {
    return accountTypeKey === "receiver";
}

function filterPlansForDisplay(allPlans, accountTypeId) {
    return (allPlans || []).slice();
}

function isPlanGreyedOutForAccount(planId, effectiveCurrentPlanId, accountTypeId, hasBigRigSubscription, isTrialOnlySubscription) {
    const operatorAccount = isOperatorAccountTypeId(accountTypeId);
    const ownerAccount = isOwnerAccountTypeId(accountTypeId);
    const normalizedCurrentPlanId = normalizePlanId(effectiveCurrentPlanId);
    const normalizedPlanId = normalizePlanId(planId);
    const operatorCurrentBigRig = operatorAccount && normalizedCurrentPlanId === PLAN_ID.bigRig;

    if (normalizedPlanId === PLAN_ID.wildcatter) return normalizedCurrentPlanId !== PLAN_ID.wildcatter || isTrialOnlySubscription;
    if (operatorCurrentBigRig) return normalizedPlanId !== PLAN_ID.bigRig;

    const currentRank = isTrialOnlySubscription ? 0 : (PLAN_RANK[normalizedCurrentPlanId] ?? 0);
    const rank = PLAN_RANK[normalizedPlanId] ?? 0;
    if (normalizedPlanId === PLAN_ID.bigRig) return hasBigRigSubscription;
    return rank < currentRank || isBlackGoldPeerLocked(normalizedPlanId, normalizedCurrentPlanId);
}

export default function MembershipCarousel5({ accountId = null, billingPeriod = 1 }) {
    const navigate = useNavigate();
    const { getAccessTokenSilently } = useAuth0();
    const outlet = useOutletContext() ?? null;
    const wrapRef = useRef(null);
    const [uiScale, setUiScale] = useState(1);

    const currentAccount = Array.isArray(outlet) ? (outlet[0] ?? null) : (outlet?.currentAccount ?? outlet?.account ?? null);
    const accountProfile = Array.isArray(outlet) ? (outlet[1] ?? null) : (outlet?.accountProfile ?? null);
    const baseUrl = Array.isArray(outlet) ? (outlet[5] ?? "") : (outlet?.baseUrl ?? "");
    const resolvedAccountId = Number(accountId ?? currentAccount?.id ?? currentAccount?.accountId ?? accountProfile?.accountId ?? accountProfile?.AccountId ?? 0);
    const accountTypeId = normalizeAccountTypeKey(accountProfile, currentAccount);

    useEffect(() => {
        const element = wrapRef.current;
        if (!element) return;

        const computeScale = () => {
            const width = element.getBoundingClientRect().width || 980;
            const stageHeight = clamp(width * CONFIG.size.stageH.ratio, CONFIG.size.stageH.min, CONFIG.size.stageH.max);
            setUiScale(clamp(stageHeight / CONFIG.uiScale.baseStageH, CONFIG.uiScale.min, CONFIG.uiScale.max));
        };

        computeScale();

        if (typeof ResizeObserver === "undefined") {
            window.addEventListener("resize", computeScale);
            return () => window.removeEventListener("resize", computeScale);
        }

        const observer = new ResizeObserver(computeScale);
        observer.observe(element);
        return () => observer.disconnect();
    }, []);

    const allPlans = useMemo(() => {
        const wildcatter = {
            id: PLAN_ID.wildcatter,
            title: "WILDCATTER",
            tier: "Free",
            price: { type: "free", label: "FREE" },
            cta: "Unavailable",
            includesLabel: "Includes:",
        };

        const blackGoldRevenue = {
            id: PLAN_ID.blackgoldRevenue,
            title: "BLACKGOLD - REVENUE",
            tier: "Premium",
            price: { type: "fixed", amount: 120, unitTop: "/Year", unitBottom: "Per Owner" },
            cta: "Upgrade to Revenue",
            includesLabel: "Includes Wildcatter Features Plus:",
        };

        const blackGoldJib = {
            id: PLAN_ID.blackgoldJib,
            title: "BLACKGOLD - JIB",
            tier: "Premium",
            price: { type: "fixed", amount: 120, unitTop: "/Year", unitBottom: "Per Owner" },
            cta: "Upgrade to JIB",
            includesLabel: "Includes Wildcatter Features Plus:",
        };

        const oilBaron = {
            id: PLAN_ID.oilBaron,
            title: "OIL BARON",
            tier: "Pro",
            badge: { text: "Most Popular", variant: "warning", textColor: "dark" },
            price: { type: "fixed", amount: 240, unitTop: "/Year", unitBottom: "Per Owner" },
            cta: "Go Pro",
            includesLabel: "Includes Wildcatter + BlackGold Features Plus:",
        };

        const bigRig = {
            id: PLAN_ID.bigRig,
            title: "BIG RIG",
            tier: "For Operators",
            price: { type: "custom", labelTop: "Custom pricing", labelBottom: "Contact us" },
            cta: "Contact Sales",
            includesLabel: "Includes everything in Oil Baron Plus:",
        };

        return [wildcatter, blackGoldRevenue, blackGoldJib, oilBaron, bigRig];
    }, []);

    const {
        subscriptionsLoading,
        subscriptionsLoadFailed,
        subscriptionStatus,
        subscriptionStatusReady,
    } = useMembershipSubscriptionStatus({
        accountId: resolvedAccountId,
        currentAccount,
        accountProfile,
        getAccessTokenSilently,
        includeCancelable: true,
        baseUrl,
    });

    const effectiveCurrentPlanId = normalizePlanId(subscriptionStatus?.effectivePlanId || PLAN_ID.wildcatter);
    const hasBigRigSubscription = Boolean(subscriptionStatus?.hasBigRigPaid);
    const hasPaidSubscription = Boolean(subscriptionStatus?.hasRevenue || subscriptionStatus?.hasJib || subscriptionStatus?.hasOilBaronPaid || subscriptionStatus?.hasBigRigPaid);
    const isTrialOnlySubscription = Boolean(subscriptionStatus?.isTrialActive) && !hasPaidSubscription;
    const normalizedCurrentPlanId = normalizePlanId(effectiveCurrentPlanId);
    const hasOperatorBigRigSubscription = isOperatorAccountTypeId(accountTypeId) && hasBigRigSubscription;
    const planAccessBaselineId = hasOperatorBigRigSubscription ? PLAN_ID.bigRig : (isTrialOnlySubscription ? PLAN_ID.wildcatter : normalizedCurrentPlanId);
    const currentPlanActionPlanId = isTrialOnlySubscription ? "" : normalizedCurrentPlanId;

    const plans = useMemo(() => filterPlansForDisplay(allPlans, accountTypeId), [allPlans, accountTypeId]);

    const isPlanGreyedOut = (planId) => isPlanGreyedOutForAccount(planId, normalizedCurrentPlanId, accountTypeId, hasBigRigSubscription, isTrialOnlySubscription);

    const launchCheckoutForPlanId = (targetPlanId) => {
        if (!targetPlanId) return;
        if (targetPlanId === effectiveCurrentPlanId && !isTrialOnlySubscription) return;

        const currentRank = PLAN_RANK[planAccessBaselineId] ?? 0;
        const targetRank = PLAN_RANK[targetPlanId] ?? 0;
        if (targetRank < currentRank) return;

        const items = {};

        if (targetPlanId === PLAN_ID.blackgoldRevenue) {
            items[STRIPE_PRICE_ID.Revenue_BlackGold] = "1";
        } else if (targetPlanId === PLAN_ID.blackgoldJib) {
            items[STRIPE_PRICE_ID.JIB_BlackGold] = "1";
        } else if (targetPlanId === PLAN_ID.oilBaron) {
            Object.assign(items, getOilBaronUpsellCheckoutItems(effectiveCurrentPlanId));
        } else {
            return;
        }

        try {
            sessionStorage.setItem("totdata_post_checkout_return", "/membership-plans");
            sessionStorage.setItem("totdata_checkout_flow", "new");
        } catch {
        }

        navigate("/payment", {
            state: {
                billingPeriod,
                items,
                useNewCheckout: true,
            },
        });
    };

    return (
        <div ref={wrapRef} className="mc-wrap" style={{ "--mc-scale": uiScale }}>
            {subscriptionsLoadFailed ? <div className="mc-sub-note">{TEXT.subscriptionsLoadFailed}</div> : null}
            {subscriptionsLoading ? <div className="mc-sub-loading text-muted">{TEXT.loadingSubscriptions}</div> : null}

            <div className="mc-matrix-anim" aria-live="polite">
                <MembershipPlansMatrix
                    plans={plans}
                    subscriptionStatus={subscriptionStatus}
                    onCheckoutPlanId={launchCheckoutForPlanId}
                    isPlanGreyedOut={isPlanGreyedOut}
                    currentPlanActionPlanId={currentPlanActionPlanId}
                    highlightEnabled={subscriptionStatusReady}
                    text={TEXT}
                />
            </div>
        </div>
    );
}
